import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCcw,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import {
  PersonChip,
  TicketPriorityChip,
  TicketRef,
  TicketStatusChip,
} from '@/components/tickets/TicketChips';
import type {
  FeedbackTaskWithContext,
  FeedbackWithContext,
} from '@/features/customerFeedback/hooks';
import {
  FEEDBACK_CHANNEL_META,
  FEEDBACK_SENTIMENT_META,
  FEEDBACK_TASK_KIND_META,
  feedbackCustomerLabel,
} from '@/features/customerFeedback/model';
import { formatDate, formatDateTime, formatRelative } from '@/lib/formatters';

interface FeedbackEntryCardProps {
  entry: FeedbackWithContext;
  canDelete: boolean;
  onEdit: (entry: FeedbackWithContext) => void;
  onDelete: (entry: FeedbackWithContext) => void;
  onProcess: (entry: FeedbackWithContext) => void;
  onAddTask: (entry: FeedbackWithContext) => void;
  onEditTask: (entry: FeedbackWithContext, task: FeedbackTaskWithContext) => void;
  onSendTask: (entry: FeedbackWithContext, task: FeedbackTaskWithContext) => void;
  onDismissTask: (task: FeedbackTaskWithContext) => void;
  onRestoreTask: (task: FeedbackTaskWithContext) => void;
  onDeleteTask: (task: FeedbackTaskWithContext) => void;
}

// Roughly how much of a pasted thread to show before "Show more".
const COLLAPSED_CHARS = 420;

/**
 * One interaction in the feed: who / how / when, the pasted text, the
 * rep's notes, then the AI pass — summary, sentiment and the extracted
 * tasks with their Send request buttons.
 */
export function FeedbackEntryCard({
  entry,
  canDelete,
  onEdit,
  onDelete,
  onProcess,
  onAddTask,
  onEditTask,
  onSendTask,
  onDismissTask,
  onRestoreTask,
  onDeleteTask,
}: FeedbackEntryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  const channel = FEEDBACK_CHANNEL_META[entry.channel];
  const ChannelIcon = channel.icon;
  const sentiment = entry.ai_sentiment ? FEEDBACK_SENTIMENT_META[entry.ai_sentiment] : null;
  const processing = entry.ai_status === 'processing';

  const long = entry.interaction.length > COLLAPSED_CHARS;
  const interactionText =
    long && !expanded
      ? `${entry.interaction.slice(0, COLLAPSED_CHARS).trimEnd()}…`
      : entry.interaction;

  const suggested = entry.tasks.filter((t) => t.status === 'suggested');
  const sent = entry.tasks.filter((t) => t.status === 'sent');
  const dismissed = entry.tasks.filter((t) => t.status === 'dismissed');
  const visibleTasks = [...suggested, ...sent, ...(showDismissed ? dismissed : [])];

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3 className="truncate text-base font-semibold leading-tight">
              {feedbackCustomerLabel(entry)}
            </h3>
            {entry.customer?.email && (
              <span className="truncate text-xs text-muted-foreground">{entry.customer.email}</span>
            )}
            {sentiment && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium',
                  sentiment.className,
                )}
                title={`Sentiment: ${sentiment.label}`}
              >
                <sentiment.icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                {sentiment.label}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ChannelIcon className="h-3.5 w-3.5" strokeWidth={1.7} />
              {channel.label}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{formatDate(entry.occurred_at)}</span>
              </TooltipTrigger>
              <TooltipContent>
                {formatDateTime(entry.occurred_at)} · logged {formatRelative(entry.created_at)}
              </TooltipContent>
            </Tooltip>
            <PersonChip person={entry.author} fallback="Former teammate" size="xs" muted />
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {entry.ai_status === 'done' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onProcess(entry)}
              disabled={processing}
              title="Run the extraction again"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
              Re-run
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="More">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(entry)}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit interaction
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onAddTask(entry)}>
                <Plus className="mr-2 h-3.5 w-3.5" />
                Add a task by hand
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onProcess(entry)} disabled={processing}>
                <Sparkles className="mr-2 h-3.5 w-3.5" />
                {entry.ai_status === 'done' ? 'Re-run extraction' : 'Extract tasks'}
              </DropdownMenuItem>
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => onDelete(entry)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete entry
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Body */}
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4 px-5 py-4 lg:border-r lg:border-border">
          <section className="space-y-1.5">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Interaction
            </h4>
            <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground/90">
              {interactionText}
            </p>
            {long && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-medium text-atmosphere-dark hover:underline"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" /> Show full interaction
                  </>
                )}
              </button>
            )}
          </section>

          {entry.rep_notes && (
            <section className="space-y-1.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Rep notes
              </h4>
              <div className="rounded-lg border-l-2 border-atmosphere-dark bg-secondary/40 px-3 py-2">
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                  {entry.rep_notes}
                </p>
              </div>
            </section>
          )}
        </div>

        <div className="space-y-4 px-5 py-4">
          <AiPanel entry={entry} onProcess={onProcess} />

          {(visibleTasks.length > 0 || dismissed.length > 0) && (
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Tasks
                  {suggested.length > 0 && (
                    <span className="ml-1.5 rounded-full bg-secondary px-1.5 py-0 text-[10px] font-medium tabular-nums text-muted-foreground">
                      {suggested.length} to review
                    </span>
                  )}
                </h4>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => onAddTask(entry)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </Button>
              </div>
              <ul className="space-y-2">
                {visibleTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onEdit={() => onEditTask(entry, task)}
                    onSend={() => onSendTask(entry, task)}
                    onDismiss={() => onDismissTask(task)}
                    onRestore={() => onRestoreTask(task)}
                    onDelete={() => onDeleteTask(task)}
                  />
                ))}
              </ul>
              {dismissed.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDismissed((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {showDismissed
                    ? 'Hide dismissed'
                    : `${dismissed.length} dismissed suggestion${dismissed.length === 1 ? '' : 's'}`}
                </button>
              )}
            </section>
          )}
        </div>
      </div>
    </Card>
  );
}

function AiPanel({
  entry,
  onProcess,
}: {
  entry: FeedbackWithContext;
  onProcess: (entry: FeedbackWithContext) => void;
}) {
  switch (entry.ai_status) {
    case 'processing':
      return (
        <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-atmosphere-dark" />
          Reading the interaction and extracting tasks…
        </div>
      );
    case 'failed':
      return (
        <div className="space-y-2 rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/10 px-3 py-3 text-sm">
          <div className="flex items-start gap-2 text-fresh-salmon-dark">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium">Extraction failed</p>
              {entry.ai_error && <p className="break-words text-xs opacity-90">{entry.ai_error}</p>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => onProcess(entry)}>
            <RefreshCcw className="h-3.5 w-3.5" />
            Try again
          </Button>
        </div>
      );
    case 'pending':
      return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border px-3 py-3">
          <p className="text-sm text-muted-foreground">Not processed yet.</p>
          <Button size="sm" onClick={() => onProcess(entry)}>
            <Sparkles className="h-3.5 w-3.5" />
            Extract tasks
          </Button>
        </div>
      );
    case 'done':
    default:
      return (
        <section className="space-y-1.5">
          <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            Summary
          </h4>
          <p className="text-sm leading-relaxed">
            {entry.ai_summary || 'Processed — no summary returned.'}
          </p>
          {entry.tasks.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Nothing actionable was found in this interaction.
            </p>
          )}
        </section>
      );
  }
}

function TaskRow({
  task,
  onEdit,
  onSend,
  onDismiss,
  onRestore,
  onDelete,
}: {
  task: FeedbackTaskWithContext;
  onEdit: () => void;
  onSend: () => void;
  onDismiss: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const kind = FEEDBACK_TASK_KIND_META[task.kind];
  const KindIcon = kind.icon;
  const isSent = task.status === 'sent';
  const isDismissed = task.status === 'dismissed';

  return (
    <li
      className={cn(
        'rounded-lg border px-3 py-2.5',
        isSent ? 'border-border bg-secondary/30' : 'border-border bg-card',
        isDismissed && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            kind.className,
          )}
          title={kind.label}
        >
          <KindIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
        </span>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn('text-sm font-medium leading-snug', isDismissed && 'line-through')}>
              {task.title}
            </span>
            <span className="text-[11px] text-muted-foreground">{kind.label}</span>
            {!isSent && (
              <TicketPriorityChip priority={task.suggested_priority} className="text-[10px]" />
            )}
            {task.source === 'manual' && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                added by hand
              </span>
            )}
          </div>
          {task.details && (
            <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">
              {task.details}
            </p>
          )}
          {task.evidence && !isSent && (
            <p className="truncate text-xs italic text-muted-foreground/80" title={task.evidence}>
              “{task.evidence}”
            </p>
          )}
          {isSent && (
            <div className="flex flex-wrap items-center gap-2 pt-0.5 text-xs">
              {task.ticket ? (
                <Link
                  to={`/tickets/${task.ticket.id}`}
                  className="inline-flex items-center gap-1.5 font-medium text-atmosphere-dark hover:underline"
                >
                  <Send className="h-3 w-3" />
                  Sent as <TicketRef number={task.ticket.number} className="text-atmosphere-dark" />
                  <ArrowUpRight className="h-3 w-3" />
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Send className="h-3 w-3" />
                  Sent {task.sent_at ? formatRelative(task.sent_at) : ''}
                </span>
              )}
              {task.ticket && (
                <TicketStatusChip status={task.ticket.status} className="text-[10px]" />
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {task.status === 'suggested' && (
            <>
              <Button size="sm" onClick={onSend} className="h-8">
                <Send className="h-3.5 w-3.5" />
                Send request
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Task options">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit}>
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Edit wording
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onDismiss}>
                    <X className="mr-2 h-3.5 w-3.5" />
                    Dismiss
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={onDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          {isDismissed && (
            <Button size="sm" variant="ghost" className="h-8" onClick={onRestore}>
              <RotateCcw className="h-3.5 w-3.5" />
              Restore
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
